import { useState } from "react";
import { Building2, Mail, SlidersHorizontal, ShieldCheck } from "lucide-react";
import { NavLink } from "react-router-dom";

export default function Settings() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          Settings
        </h1>
        <p className="text-slate-500 mt-1">
          Manage business details, response rules, and notification preferences.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-5">
              <Building2 size={20} className="text-indigo-600" />
              <h2 className="text-lg font-semibold text-slate-900">
                Business Profile
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Business Name" value="Joe's Coffee Shop" />
              <Field label="Location" value="Columbus, OH" />
              <Field label="Website" value="https://joescoffee.com" />
              <Field label="Phone" value="(614) 555-0198" />
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-5">
              <SlidersHorizontal size={20} className="text-indigo-600" />
              <h2 className="text-lg font-semibold text-slate-900">
                Response Preferences
              </h2>
            </div>

            <div className="space-y-4">
              <SelectField
                label="Default Tone"
                value="Professional and friendly"
                options={[
                  "Professional and friendly",
                  "Warm and casual",
                  "Short and simple",
                ]}
              />

              <SelectField
                label="Response Length"
                value="Medium"
                options={["Short", "Medium", "Detailed"]}
              />

              <ToggleRow
                title="Auto-generate responses"
                description="Create suggested responses when new reviews arrive."
                enabled
              />

              <ToggleRow
                title="Require approval before posting"
                description="Responses must be approved before being published."
                enabled
              />

              <ToggleRow
                title="Manual review for negative reviews"
                description="Always flag 1–3 star reviews for manual approval."
                enabled
              />
            </div>
          </section>
        </div>

        <div className="space-y-6">
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-5">
              <Mail size={20} className="text-indigo-600" />
              <h2 className="text-lg font-semibold text-slate-900">
                Notifications
              </h2>
            </div>

            <div className="space-y-4">
              <ToggleRow
                title="Approval emails"
                description="Email owner when a response needs approval."
                enabled
              />

              <ToggleRow
                title="New review alerts"
                description="Notify when a new Google review comes in."
                enabled
              />

              <ToggleRow
                title="Weekly summary"
                description="Send a weekly performance report."
              />
            </div>
          </section>

          <section className="bg-indigo-600 text-white rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={20} />
              <h2 className="text-lg font-semibold">Google Connected</h2>
            </div>

            <p className="text-sm text-indigo-100">
              Joe's Coffee Shop is connected to Google Business Profile.
            </p>

            <button className="mt-4 bg-white text-indigo-700 hover:bg-indigo-50 px-4 py-2 rounded-xl text-sm font-medium transition">
              Manage Connection
            </button>
          </section>
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              Legal & Privacy
            </h2>

            <div className="space-y-3">
              <NavLink
                to="/legal/terms"
                className="flex items-center justify-between rounded-xl border border-slate-200 p-4 hover:bg-slate-50 transition"
              >
                <div>
                  <p className="font-medium text-slate-900">Terms of Service</p>

                  <p className="text-sm text-slate-500">
                    Review the platform terms and usage agreement.
                  </p>
                </div>
              </NavLink>

              <NavLink
                to="/legal/privacy"
                className="flex items-center justify-between rounded-xl border border-slate-200 p-4 hover:bg-slate-50 transition"
              >
                <div>
                  <p className="font-medium text-slate-900">Privacy Policy</p>

                  <p className="text-sm text-slate-500">
                    Learn how RevUShield stores and uses data.
                  </p>
                </div>
              </NavLink>
            </div>
          </section>
        </div>
      </div>

      <div className="flex justify-end">
        <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition">
          Save Changes
        </button>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <label className="space-y-1.5">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input
        defaultValue={value}
        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    </label>
  );
}

function SelectField({ label, value, options }) {
  return (
    <label className="space-y-1.5 block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <select
        defaultValue={value}
        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        {options.map((option) => (
          <option key={option}>{option}</option>
        ))}
      </select>
    </label>
  );
}

function ToggleRow({ title, description, defaultEnabled = false }) {
  const [enabled, setEnabled] = useState(defaultEnabled);

  return (
    <div className="flex items-start sm:items-center justify-between gap-4 rounded-2xl border border-slate-200 p-4">
      <div>
        <h3 className="font-medium text-slate-900">{title}</h3>

        <p className="text-sm text-slate-500 mt-1">{description}</p>
      </div>

      <button
        onClick={() => setEnabled(!enabled)}
        className={`
          relative h-8 w-14 rounded-full transition shrink-0
          ${enabled ? "bg-indigo-600" : "bg-slate-300"}
        `}
      >
        <span
          className={`
            absolute top-1 h-6 w-6 rounded-full bg-white shadow-sm transition
            ${enabled ? "left-7" : "left-1"}
          `}
        />
      </button>
    </div>
  );
}
